/*
  Kalmora — product.js
  Ficha de producto: variantes y galería. Sin dependencias.

  - Variantes: al elegir una (radio o select con name="id") se actualizan el precio, el botón
    (texto, precio y disponibilidad), la barra fija, la URL (?variant=, con replaceState)
    y la imagen de la galería asociada a esa variante.
  - Galería: carrusel con scroll-snap nativo (swipe en móvil), contador y miniaturas.
  Sin JavaScript todo sigue funcionando: el formulario envía la variante elegida.
*/
(() => {
  function initProduct(section) {
    if (section.dataset.productReady) return;
    section.dataset.productReady = 'true';

    const dataEl = section.querySelector('[data-product-variants]');
    const variants = dataEl ? JSON.parse(dataEl.textContent) : [];
    const form = section.querySelector('form[action*="/cart/add"]');
    const track = section.querySelector('[data-gallery-track]');
    const items = track ? Array.from(track.children) : [];
    const thumbs = Array.from(section.querySelectorAll('[data-gallery-thumb]'));
    const counter = section.querySelector('[data-gallery-current]');
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    /* ── Galería ── */
    function setActive(index) {
      thumbs.forEach((thumb, i) => {
        thumb.classList.toggle('is-active', i === index);
        if (i === index) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
      if (counter) counter.textContent = String(index + 1);
    }

    function goTo(index) {
      const item = items[index];
      if (!item || !track) return;
      const behavior = reduceMotion.matches ? 'auto' : 'smooth';
      track.scrollTo({ left: item.offsetLeft - track.offsetLeft, behavior });
      setActive(index);
    }

    if (track && items.length > 1) {
      // El elemento más visible del carrusel es el activo (sigue al swipe del dedo)
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.intersectionRatio > 0.6) setActive(items.indexOf(entry.target));
          });
        },
        { root: track, threshold: [0.6] }
      );
      items.forEach((item) => observer.observe(item));
      thumbs.forEach((thumb) => thumb.addEventListener('click', () => goTo(Number(thumb.dataset.galleryThumb))));
    }

    /* ── Variantes ── */
    if (!form || variants.length === 0) return;

    const addButton = form.querySelector('[data-add-button]');
    const addLabel = form.querySelector('[data-add-label]');
    const addPrice = form.querySelector('[data-add-price]');
    const addPriceValue = form.querySelector('[data-add-price-value]');
    const priceEl = section.querySelector('[data-product-price]');
    const priceCompactEl = section.querySelector('[data-product-price-compact]');
    const stickyAdd = section.querySelector('[data-sticky-add]');
    const strings = {
      add: addButton ? addButton.dataset.labelAdd || '' : '',
      soldOut: addButton ? addButton.dataset.labelSoldOut || '' : '',
    };

    /* ── Selector de packs (snippets/bundle-picker.liquid) ── */
    const bundleEl = section.querySelector('[data-bundle]');
    const bundleDataEl = bundleEl ? bundleEl.querySelector('[data-bundle-data]') : null;
    const bundle = bundleDataEl ? JSON.parse(bundleDataEl.textContent) : null;
    const paymentButton = form.querySelector('.shopify-payment-button');

    function bundleRow(variantId) {
      if (!bundle) return null;
      const checked = bundleEl.querySelector('input[type="radio"]:checked');
      const options = bundle[String(variantId)];
      return checked && options ? options[checked.value] : null;
    }

    function priceHtml(row, extraClass) {
      const sale = Boolean(row.compare);
      return '<div class="price' + (sale ? ' price--on-sale' : '') + ' ' + extraClass + '"><span class="price__current">' +
        row.price + '</span>' + (sale ? ' <s class="price__compare">' + row.compare + '</s>' : '') + '</div>';
    }

    function selectedId() {
      const checked = form.querySelector('input[name="id"]:checked');
      if (checked) return Number(checked.value);
      const field = form.querySelector('select[name="id"], input[name="id"]');
      return field ? Number(field.value) : null;
    }

    function update(initial) {
      const variant = variants.find((v) => v.id === selectedId());
      if (!variant) return;

      // Con selector de packs, el precio y la disponibilidad son los de la tarjeta elegida
      const row = bundleRow(variant.id);
      const available = row ? row.available : variant.available;
      if (row) {
        if (priceEl) priceEl.innerHTML = priceHtml(row, 'price--large');
        if (priceCompactEl) priceCompactEl.innerHTML = priceHtml(row, 'sticky-buy__price');
        bundleEl.querySelectorAll('[data-bundle-price]').forEach((el) => {
          const option = bundle[String(variant.id)][el.dataset.bundlePrice];
          if (option) el.textContent = option.price;
        });
        // Shop Pay compra la variante del formulario tal cual: solo se ofrece con "1 unidad"
        if (paymentButton) paymentButton.hidden = bundleEl.querySelector('input:checked').value !== 'single';
      } else {
        if (priceEl) priceEl.innerHTML = variant.priceHtml;
        if (priceCompactEl) priceCompactEl.innerHTML = variant.priceCompactHtml;
      }

      const label = available ? strings.add : strings.soldOut;
      if (addButton) addButton.disabled = !available;
      if (addLabel && label) addLabel.textContent = label;
      if (addPrice) addPrice.hidden = !available;
      if (addPriceValue) addPriceValue.textContent = row ? row.price : variant.price;
      if (stickyAdd) {
        stickyAdd.disabled = !available;
        if (label) stickyAdd.textContent = label;
      }

      // Al cargar solo se sincronizan precios (p. ej. si el navegador recuerda otra tarjeta)
      if (initial === true) return;

      if (section.dataset.urlSync === 'false') return;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(variant.id));
      window.history.replaceState({}, '', url.toString());

      if (variant.media) {
        const index = items.findIndex((item) => item.dataset.mediaKey === String(variant.media));
        if (index > -1) goTo(index);
      }
    }

    form.addEventListener('change', (event) => {
      if (event.target.name === 'id') update();
    });

    if (bundle) {
      bundleEl.addEventListener('change', () => update());
      // Al enviar (assets/cart.js crea FormData con el formulario), se añade la variante y cantidad de la tarjeta
      form.addEventListener('formdata', (event) => {
        const row = bundleRow(selectedId());
        if (!row) return;
        event.formData.set('id', String(row.id));
        event.formData.set('quantity', String(row.qty));
      });
      update(true);
    }
  }

  function initAll(scope) {
    scope.querySelectorAll('[data-product-section]').forEach(initProduct);
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  initAll(document);
})();
